import { Box, Typography, useTheme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import React, { useLayoutEffect, useRef } from "react";

import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { useActiveLearner } from "../../../providers/ActiveLearnerProvider";
import { HEADER_WIDTH } from "../../nav/Header";
import { getIconButtonBaseStyles, getInteractiveStyles } from "../constants";
import { useUpsertLearner } from "../hooks/useUpsertLearner";
import AddLearnerButton from "./AddLearnerButton";
import AddLearnerModal from "./AddLearnerModal";
import InlineAddLearnerButton from "./InlineAddLearnerButton";

interface LearnerBannerButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  selected: boolean;
}

function LearnerBannerButton({
  children,
  onClick,
  selected,
}: LearnerBannerButtonProps) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        ...getIconButtonBaseStyles(theme),
        ...getInteractiveStyles(theme, selected),
        px: 18,
        [theme.breakpoints.down("md")]: {
          height: 48,
          px: 18,
        },
      }}
    >
      {children}
    </Box>
  );
}

interface LearnerBannerProps {
  setLearnerBannerHeight: (height: number) => void;
}

export default function LearnerBanner({
  setLearnerBannerHeight,
}: LearnerBannerProps) {
  const { currentUser } = useSession();
  const { headerHeight } = useStickyHeader();
  const bannerRef = useRef<HTMLDivElement>(null);
  const { activeLearner, setActiveLearner } = useActiveLearner();
  const { t } = useTranslation(
    "client\\components\\parentDashboardV2\\components\\LearnerBanner",
  );
  const theme = useTheme();
  const { openModal, ...rest } = useUpsertLearner();
  const learners = currentUser?.children ?? [];

  useLayoutEffect(() => {
    if (bannerRef.current) {
      setLearnerBannerHeight(bannerRef.current.getBoundingClientRect().height);
    }
  }, [bannerRef, setLearnerBannerHeight]);

  return (
    <>
      <Box sx={{ height: "1px", backgroundColor: theme.palette.grey[200] }} />
      <Box
        ref={bannerRef}
        sx={{
          backgroundColor: "white",
          position: "sticky",
          top: headerHeight,
          zIndex: 200,
          boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)",
          transition: "box-shadow 0.2s ease-in-out",
        }}
      >
        <Box
          flex
          alignItems="center"
          sx={{
            gap: 20,
            py: 16,
            maxWidth: HEADER_WIDTH,
            mx: "auto",
            "@media (max-width: 1440px)": {
              px: 24,
            },
            ...(!learners.length && {
              justifyContent: "center",
            }),
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "2.4rem",
              pointerEvents: "none",
              zIndex: 1,
            },
            "&:before": {
              left: 0,
              background: `linear-gradient(to right, ${theme.palette.grey[50]}, transparent)`,
            },
            "&:after": {
              right: 0,
              background: `linear-gradient(to left, ${theme.palette.grey[50]}, transparent)`,
            },
            [theme.breakpoints.down("md")]: {
              "&:before, &:after": {
                width: "1.6rem",
              },
              "&:before": {
                background: `linear-gradient(to right, ${theme.palette.grey[50]}, transparent)`,
              },
              "&:after": {
                background: `linear-gradient(to left, ${theme.palette.grey[50]}, transparent)`,
              },
            },
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            [theme.breakpoints.down("md")]: {
              px: 18,
              py: 14,
            },
          }}
        >
          {!learners.length && <AddLearnerButton />}
          {learners.length > 1 && (
            <LearnerBannerButton
              onClick={() => setActiveLearner(null)}
              selected={activeLearner === null}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  [theme.breakpoints.down("md")]: {
                    fontSize: "1.4rem",
                  },
                }}
              >
                {t("All Learners")}
              </Typography>
            </LearnerBannerButton>
          )}
          {learners.map(learner => (
            <LearnerBannerButton
              key={learner.uid}
              onClick={() => setActiveLearner(learner.uid)}
              selected={activeLearner?.uid === learner.uid}
            >
              <Box
                flex
                alignItems="center"
                justifyContent="center"
                sx={{
                  gap: 18,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    [theme.breakpoints.down("md")]: { display: "none" },
                  }}
                >
                  <AvatarImage
                    avatar={learner.avatar || DEFAULT_AVATAR}
                    width={40}
                    height={40}
                  />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    [theme.breakpoints.down("md")]: {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  {learner.name}
                </Typography>
              </Box>
            </LearnerBannerButton>
          ))}
          {learners.length > 0 && (
            <>
              <InlineAddLearnerButton
                showLabel={learners.length === 1}
                onClick={() => {
                  openModal();
                }}
              />
              <AddLearnerModal {...rest} />
            </>
          )}
        </Box>
      </Box>
    </>
  );
}
